<script>
import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import ContentsWithLinks from "@/components/article/politiken/ContentsWithLinks.vue";
import lang from "@/directives/lang";

import ArticleContentElaborations from "./ArticleContentElaborations.vue";
import ArticleContentHeader from "./ArticleContentHeader.vue";

export default {
  name: "SimplifiedCurrentArticleContent",
  components: {
    ArticleContent,
    ArticleContentElaborations,
    ArticleContentHeader,
    ArticleContentCollapser,
    ContentsWithLinks
  },
  directives: {
    lang
  },
  props: {
    article: {
      type: Object,
      required: true
    },
    hasReadingLines: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    isCollapsible: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<template>
  <ArticleContent>
    <ArticleContentHeader :word="article.word" />

    <ArticleContentCollapser :is-collapsible="isCollapsible">
      <div
        class="article-content__body-container"
        :class="{ 'article-content__body-container--reading-lines': hasReadingLines }"
      >
        <ArticleContentElaborations :word="article.word" />

        <div
          v-if="
            article.word.wordClass ||
            article.word.flexion ||
            article.word.alternativeSpelling ||
            article.word.syllable ||
            article.word.abbreviation
          "
          class="article-content__spacer"
        />

        <div
          v-for="meaning in article.meanings"
          :key="`${meaning.lemma}_${meaning.id}`"
          class="indentation-container"
        >
          <div class="indentation-container">
            <span
              v-if="meaning.meaningNumber > 0"
              class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
            >
              {{ meaning.meaningNumber }}.
            </span>

            <span
              v-if="meaning.sublemma"
              class="article-content__text article-content__text--primary article-content__text--prominent"
            >
              <b v-html="meaning.sublemma"></b>
              =
            </span>

            <span v-if="meaning.rComment"> ({{ meaning.rComment }}) </span>

            <span
              v-if="meaning.synonymDefinition.length"
              class="article-content__text article-content__text--primary article-content__text--prominent"
            >
              <ContentsWithLinks
                v-for="(synonymDefinition, index) in meaning.synonymDefinition"
                :key="`synonymDef_${index}`"
                :content="synonymDefinition"
                link-class="article-content__text--link"
              ></ContentsWithLinks>
            </span>

            <span
              v-if="meaning.definition"
              class="article-content__text"
              :class="{
                'article-content__text--primary article-content__text--prominent': !meaning.sublemma
              }"
              v-html="meaning.definition"
            />

            <span v-if="meaning.synonym.length" class="pl-1">
              <ContentsWithLinks
                v-for="(synonym, index) in meaning.synonym"
                :key="`synonym_${index}`"
                :content="synonym"
                link-class="article-content__text--link"
              ></ContentsWithLinks>
            </span>

            <div
              v-if="meaning.example"
              class="article-content__text article-content__text--secondary-light article-content__text--italic"
            >
              &#9671; <span v-html="meaning.example" />
            </div>

            <div v-if="meaning.abbreviation" class="article-content__politiken-grid">
              <span class="article-content__text article-content__politiken-grid-label"
                >Forkortes:</span
              >
              <span
                class="article-content__text article-content__text--primary article-content__text--prominent article-content__politiken-grid-content"
              >
                <span v-html="meaning.abbreviation" />
              </span>
            </div>

            <div
              v-if="meaning.compoundOne.length || meaning.compoundTwo.length"
              class="article-content__politiken-grid"
            >
              <span class="article-content__text article-content__politiken-grid-label"
                >Sammensætning:</span
              >
              <span class="article-content__politiken-grid-content">
                <span
                  v-for="(compound, index) in meaning.compoundOne"
                  :key="`compoundOne_${index}`"
                  class="article-content__text article-content__text--primary"
                >
                  <span v-if="index !== 0"> &#183; </span>
                  <ContentsWithLinks
                    :content="compound"
                    link-class="article-content__text--link"
                  ></ContentsWithLinks>
                </span>
                <span
                  v-for="(compound, index) in meaning.compoundTwo"
                  :key="`compoundTwo_${index}`"
                  class="article-content__text article-content__text--primary"
                >
                  <span v-if="meaning.compoundOne.length > 0 || index !== 0"> &#183; </span>
                  <ContentsWithLinks
                    :content="compound"
                    link-class="article-content__text--link"
                  ></ContentsWithLinks>
                </span>
              </span>
            </div>

            <div v-if="meaning.confer" class="article-content__politiken-grid">
              <span class="article-content__text article-content__politiken-grid-label">
                Jævnfør:
              </span>
              <span class="article-content__politiken-grid-content">{{ meaning.confer }}</span>
            </div>

            <div v-if="meaning.antonym.length" class="article-content__politiken-grid">
              <span class="article-content__text article-content__politiken-grid-label">
                Modsat:
              </span>
              <span class="article-content__politiken-grid-content">
                <ContentsWithLinks
                  v-for="(antonym, index) in meaning.antonym"
                  :key="`antonym_${index}`"
                  :content="antonym"
                  link-class="article-content__text--link"
                ></ContentsWithLinks>
              </span>
            </div>
          </div>
        </div>
        <div class="article-content__spacer" />
      </div>
      <div v-if="article.word.box" class="article-content__body-container">
        <div class="article-content__politiken-box">
          <div
            class="article-content__politiken-box-header"
            :class="{ 'article-content__politiken-box-header--primary': article.word.boxColored }"
            v-html="article.word.boxHeadline"
          />
          <div class="article-content__politiken-box-content" v-html="article.word.box" />
        </div>
        <div class="article-content__spacer" />
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>
